import { getVideoReferenceAudioError } from "~/common/video/videoModels"
import { post, type RequestOptions } from "~/common/request"
import {
  validateVideoGenerationRequestBodySize,
  VideoGenerationRequestValidationError,
  type VideoGenerationResource,
} from "~/common/video/videoGenerationRequest"

export { VideoGenerationRequestValidationError } from "~/common/video/videoGenerationRequest"

export const createHotVideoTask = (
  params: API.Task.CreateHotVideoTaskParams = {},
) => {
  return post<API.Task.HotVideoTask>("/api/task/hot-video/create", params)
}

export const createHotVideoWhiteImage = (
  params: API.Task.HotVideoWhiteImageTaskParams,
) => {
  return post<API.Task.HotVideoTask>("/api/task/hot-video/white-image", params)
}

export const createHotVideoModelFigureArtline = (
  params: API.Task.HotVideoModelFigureArtlineParams,
) => {
  return post<API.Task.HotVideoTask>(
    "/api/task/hot-video/model-figure-artline",
    params,
  )
}

export const parseAndOptimizeHotVideoPrompt = (
  params: API.Task.ParseAndOptimizePromptParams,
) => {
  return post<API.Task.HotVideoTask>(
    "/api/task/hot-video/parse-and-optimize-prompt",
    params,
  )
}

export const saveHotVideoOptimizePrompt = (
  params: API.Task.SaveOptimizePromptParams,
  options?: RequestOptions,
) => {
  return post<API.Task.HotVideoTask>(
    "/api/task/hot-video/save-optimize-prompt",
    params,
    options,
  )
}

export const createHotVideo = async (
  params: API.Task.CreateVideoTaskParams,
  resources: VideoGenerationResource[] = [],
) => {
  // 模特由后端追加，音色使用本次提交值；体积校验需包含全部实际资源。
  await validateVideoGenerationRequestBodySize(
    {
      ...params,
      audio_urls: params.timbre_url ? [params.timbre_url] : [],
      image_urls: Array.from(
        new Set([
          ...params.image_urls,
          ...resources
            .filter(({ url }) => url !== params.timbre_url)
            .map(({ url }) => url),
        ]),
      ),
    },
    resources,
  )

  return post<API.Task.HotVideoTask>("/api/task/hot-video/create-video", params)
}

export const copyHotVideoTask = (params: API.Task.CopyHotVideoTaskParams) => {
  return post<API.Task.HotVideoTask>("/api/task/hot-video/copy", params)
}

export const incrementHotVideoDownloadCount = (
  params: API.Task.DownloadCountParams,
) => {
  return post<API.Task.HotVideoTask>(
    "/api/task/hot-video/download-count",
    params,
  )
}

export const createDirectVideo = async (
  params: API.Task.DirectCreateVideoParams,
  resources: VideoGenerationResource[] = [],
) => {
  const audioError = await getVideoReferenceAudioError(
    params.channel,
    params.audio_urls ?? [],
  )
  if (audioError) throw new VideoGenerationRequestValidationError(audioError)

  await validateVideoGenerationRequestBodySize(params, resources)

  return post<API.Task.CreateVideoTask>("/api/task/direct-video/create", params)
}

export const incrementDirectVideoDownloadCount = (
  params: API.Task.DownloadCountParams,
) => {
  return post<API.Task.CreateVideoTask>(
    "/api/task/direct-video/download-count",
    params,
  )
}

export const createOneClickCopyTask = async (
  params: API.Task.OneClickCopyTaskParams,
) => {
  const audioError = await getVideoReferenceAudioError(
    params.channel,
    params.audio_urls ?? [],
  )
  if (audioError) throw new VideoGenerationRequestValidationError(audioError)

  await validateVideoGenerationRequestBodySize(params)

  return post<API.Task.OneClickCopyTask>(
    "/api/task/one-click-copy/create",
    params,
  )
}

export const createOneClickFavorite = (
  params: API.Task.OneClickFavoriteParams,
  options?: RequestOptions,
) => {
  return post<API.Task.OneClickFavorite>(
    "/api/task/one-click-favorite/create",
    params,
    options,
  )
}

export const cancelOneClickFavorite = (
  params: API.Task.OneClickFavoriteParams,
  options?: RequestOptions,
) => {
  return post<API.Task.OneClickFavorite>(
    "/api/task/one-click-favorite/cancel",
    params,
    options,
  )
}

export const listOneClickFavorites = (
  params: API.Task.OneClickFavoriteListParams = {},
  options?: RequestOptions,
) => {
  return post<API.Task.OneClickFavoriteListResponse>(
    "/api/task/one-click-favorite/list",
    params,
    options,
  )
}

export const incrementOneClickCopyDownloadCount = (
  params: API.Task.DownloadCountParams,
) => {
  return post<API.Task.OneClickCopyTask>(
    "/api/task/one-click-copy/download-count",
    params,
  )
}

export const listDirectVideoTasks = (params: API.Task.PageParams = {}) => {
  return post<API.Task.CreateVideoTaskListResponse>(
    "/api/task/direct-video/list",
    params,
  )
}

export const listOneClickCopyTasks = (params: API.Task.PageParams = {}) => {
  return post<API.Task.OneClickCopyTaskListResponse>(
    "/api/task/one-click-copy/list",
    params,
  )
}

export const getHotVideoDetail = (
  params: API.Task.HotVideoTaskDetailParams,
) => {
  return post<API.Task.HotVideoTask>("/api/task/hot-video/detail", params)
}

export const updateHotVideoFrontendRemark = (
  params: API.Task.UpdateHotVideoFrontendRemarkParams,
) => {
  return post<API.Task.HotVideoTask>(
    "/api/task/hot-video/frontend-remark",
    params,
  )
}

export const createParseVideoTask = (
  params: API.Task.CreateParseVideoTaskParams,
  signal?: AbortSignal,
) => {
  return post<API.Task.ParseVideoTask>("/api/task/parse-video/create", params, {
    signal,
  })
}

export const createEnhanceVideoTask = (
  params: API.Task.CreateEnhanceVideoTaskParams,
) => {
  return post<API.Task.EnhanceVideoTask>(
    "/api/task/enhance-video/create",
    params,
  )
}

export const listEnhanceVideoTasks = (
  params: API.Task.EnhanceVideoTaskListParams = {},
) => {
  return post<API.Task.EnhanceVideoTaskListResponse>(
    "/api/task/enhance-video/list",
    params,
  )
}

export const incrementEnhanceVideoDownloadCount = (
  params: API.Task.DownloadCountParams,
) => {
  return post<API.Task.EnhanceVideoTask>(
    "/api/task/enhance-video/download-count",
    params,
  )
}

export const listEraseVideoSubtitleTasks = (
  params: API.Task.EraseVideoSubtitleTaskListParams = {},
  signal?: AbortSignal,
) => {
  return post<API.Task.EraseVideoSubtitleTaskListResponse>(
    "/api/task/erase-video-subtitle/list",
    params,
    { notify: false, signal },
  )
}

export const createEraseVideoSubtitleTask = (
  params: API.Task.CreateEraseVideoSubtitleTaskParams,
) => {
  return post<API.Task.EraseVideoSubtitleTask>(
    "/api/task/erase-video-subtitle/create",
    params,
  )
}

export const incrementEraseVideoSubtitleDownloadCount = (
  params: API.Task.DownloadCountParams,
) => {
  return post<API.Task.EraseVideoSubtitleTask>(
    "/api/task/erase-video-subtitle/download-count",
    params,
    { notify: false },
  )
}

export const listParseVideoTasks = (
  params: API.Task.ParseVideoTaskListParams = {},
  signal?: AbortSignal,
) => {
  return post<API.Task.ParseVideoTaskListResponse>(
    "/api/task/parse-video/list",
    params,
    { signal },
  )
}

export const createWhiteImageTask = (
  params: API.Task.CreateWhiteImageTaskParams,
) => {
  return post<API.Task.ImageTask>("/api/task/white-image/create", params)
}

export const listWhiteImageTasks = (
  params: API.Task.ImageTaskListParams = {},
) => {
  return post<API.Task.ImageTaskListResponse>(
    "/api/task/white-image/list",
    params,
  )
}

export const listHotVideoTasks = (params: API.Task.PageParams = {}) => {
  return post<API.Task.HotVideoTaskListResponse>(
    "/api/task/hot-video/list",
    params,
  )
}

export const resolvePlayUrl = (
  params: API.Task.ResolvePlayUrlParams,
  signal?: AbortSignal,
) => {
  return post<API.Task.ResolvePlayUrlResponse>(
    "/api/task/resolve-play-url",
    params,
    { signal },
  )
}
